import {
  IServiceProviderProfile,
  ICreateServiceProviderProfileRequest,
  IServiceProviderStripeOnboardingResponse,
  IVisitFee,
} from '../interfaces/heavyDutyMachinery/IServiceProviderProfile';
import { IAssistantGuide } from '../interfaces/heavyDutyMachinery/IAssistantGuide';
import { apiService } from './ApiService';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { HTTP_METHODS } from '../constants/httpMethods';

const heavyDutyMachineryBaseUrl = process.env.REACT_APP_HEAVY_DUTY_MACHINERY_API_URL || '';

// Same bare-404-means-"flag off or no profile yet" ambiguity as
// ServiceService's own NO_SERVICE_PROVIDER_PROFILE_ERROR -- this endpoint
// itself is what's 404ing here, not a downstream Service lookup, so a bare
// 404 (no message) only ever means FeatureFlagKeys.ServicesEnabled is off.
const isNotFound = (error?: string): boolean => !!error && error.startsWith('HTTP Error: 404');

/** Returns undefined (not an error) when the caller has no ServiceProviderProfile yet -- same convention as HostProfileService.getMyHostProfile. */
export const getMyServiceProviderProfile = async (): Promise<IServiceProviderProfile | undefined> => {
  const response = await apiService<IServiceProviderProfile>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SERVICE_PROVIDER_PROFILE_ME,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  if (isNotFound(response.error)) return undefined;
  throw new Error(response.error || 'Failed to load service provider profile');
};

export const createServiceProviderProfile = async (request: ICreateServiceProviderProfileRequest): Promise<IServiceProviderProfile> => {
  const response = await apiService<IServiceProviderProfile>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SERVICE_PROVIDER_PROFILE_CREATE,
    method: HTTP_METHODS.POST,
    body: { ...request },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to create service provider profile');
};

/**
 * Creates the provider's Stripe Connect Express account the first time it's
 * called, then always returns a fresh Account Session client secret -- same
 * "Connect.js calls this itself via fetchClientSecret" pattern as
 * HostProfileService.startHostVerification. Independent-provider-only: the
 * backend rejects this outright for an employed provider (hostUserId set).
 */
export const startServiceProviderVerification = async (): Promise<IServiceProviderStripeOnboardingResponse> => {
  const response = await apiService<IServiceProviderStripeOnboardingResponse>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SERVICE_PROVIDER_PROFILE_VERIFICATION_START,
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to start verification');
};

/**
 * The professional's own standing questions -- what they always need to know
 * before they can quote.
 *
 * **Never comes back empty on a first read.** Somebody who has never touched
 * it gets the shared defaults with isDefault true, because a blank box
 * labelled "write down what you need to know" is a box nobody fills in. See
 * AssistantGuideEditor.
 *
 * undefined (not an error) when there is no provider profile yet, same
 * convention as getMyServiceProviderProfile above.
 */
export const getMyAssistantGuide = async (): Promise<IAssistantGuide | undefined> => {
  const response = await apiService<IAssistantGuide>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SERVICE_PROVIDER_ASSISTANT_GUIDE,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  if (isNotFound(response.error)) return undefined;
  throw new Error(response.error || 'Failed to load the assistant guide');
};

/**
 * Replaces the whole set, which is what the screen is showing them: they are
 * confirming a list, not patching one.
 *
 * An empty array is a real answer -- "don't ask anything standing on my
 * behalf" -- and is stored as such rather than quietly restoring the defaults.
 */
export const updateMyAssistantGuide = async (questions: string[]): Promise<IAssistantGuide> => {
  const response = await apiService<IAssistantGuide>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SERVICE_PROVIDER_ASSISTANT_GUIDE,
    method: HTTP_METHODS.PUT,
    body: { questions },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to save the assistant guide');
};

/**
 * Whether going to look at the job is charged for, and how much.
 *
 * The whole setting goes up together, because the two halves only mean
 * something as a pair. Turning it off zeroes the figure server-side rather than
 * keeping it: if it is ever switched back on, that should be a decision made
 * with a number in front of them, not one they set months ago.
 *
 * Changes nothing about requests already sent -- each carries the figure it was
 * sent under (see IServiceRequest.visitFeeAmount).
 */
export const updateMyVisitFee = async (chargesVisitFee: boolean, visitFeeAmount: number): Promise<IVisitFee> => {
  const response = await apiService<IVisitFee>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SERVICE_PROVIDER_VISIT_FEE,
    method: HTTP_METHODS.PUT,
    body: { chargesVisitFee, visitFeeAmount },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to save the visit fee');
};
